import React, { useEffect, useRef, useState } from 'react';
import { Select } from 'antd';
import { consultarEmpleados } from '../../api/empleadosApi';
import type { EmpleadoResumen } from '../../api/empleadosApi';

/** Espera entre la última tecla y la búsqueda en el servidor. */
export const ESPERA_BUSQUEDA_MS = 250;

interface Props {
  id: string;
  value?: number;
  onChange: (idEmpleado: number | undefined, empleado?: EmpleadoResumen) => void;
  /** Empleados que no pueden salir como opción (el propio, los que ya están). */
  excluir?: number[];
  placeholder?: string;
  /** Nombre accesible si no lo da una etiqueta enlazada por `id`. */
  etiqueta?: string;
  disabled?: boolean;
}

/** Selector con búsqueda entre los empleados activos, por orden alfabético. El texto buscado viaja por POST, nunca en la URL. */
export const VisibilidadSelectorEmpleado: React.FC<Props> = ({ id, value, onChange, excluir = [], placeholder = 'Busca por nombre', etiqueta, disabled }) => {
  const [opciones, setOpciones] = useState<EmpleadoResumen[]>([]);
  const [elegido, setElegido] = useState<EmpleadoResumen | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(false);
  const [texto, setTexto] = useState('');
  const ultima = useRef(0);

  useEffect(() => {
    const n = ++ultima.current;
    const t = setTimeout(async () => {
      setCargando(true);
      setError(false);
      try {
        const r = await consultarEmpleados({ texto: texto.trim() || undefined, estado: 'activos', tamano: 25, orden: { campo: 'nombre', direccion: 'asc' } });
        if (n === ultima.current) setOpciones(r.elementos);
      } catch {
        if (n === ultima.current) { setOpciones([]); setError(true); }
      } finally {
        if (n === ultima.current) setCargando(false);
      }
    }, texto ? ESPERA_BUSQUEDA_MS : 0);
    return () => clearTimeout(t);
  }, [texto]);

  const visibles = opciones.filter((o) => !excluir.includes(o.idEmpleado));
  const lista = elegido && !visibles.some((o) => o.idEmpleado === elegido.idEmpleado) ? [elegido, ...visibles] : visibles;
  const ordenadas = [...lista].sort((a, b) => a.nombreCompleto.localeCompare(b.nombreCompleto, 'es'));

  return (
    <Select
      id={id}
      aria-label={etiqueta}
      showSearch
      allowClear
      disabled={disabled}
      value={value}
      placeholder={placeholder}
      filterOption={false}
      loading={cargando}
      onSearch={setTexto}
      onChange={(v: number | undefined) => {
        const e = ordenadas.find((o) => o.idEmpleado === v);
        setElegido(e ?? null);
        onChange(v, e);
      }}
      notFoundContent={error ? 'No se ha podido buscar. Inténtalo de nuevo.' : cargando ? 'Buscando…' : 'Ningún empleado activo coincide'}
      options={ordenadas.map((o) => ({ value: o.idEmpleado, label: o.nombreCompleto }))}
      style={{ width: '100%' }}
    />
  );
};
